import { jsx, jsxs } from "react/jsx-runtime";
import assert from "assert";
import { Box } from "../Box/Box.mjs";
import { useBackground } from "../Box/BackgroundContext.mjs";
import "../Text/TextContext.mjs";
import "../Heading/HeadingContext.mjs";
import "../../css/lineHeightContainer.css.mjs";
import { buildDataAttributes } from "../private/buildDataAttributes.mjs";
import "../../css/reset/reset.css.mjs";
import "../../css/atoms/sprinkles.css.mjs";
import "../../hooks/useIcon/icon.css.mjs";
import "../../css/typography.css.mjs";
import { IconChevronDown } from "../icons/IconChevronDown/IconChevronDown.mjs";
import { IconChevronLeft } from "../icons/IconChevronLeft/IconChevronLeft.mjs";
import { IconChevronRight } from "../icons/IconChevronRight/IconChevronRight.mjs";
import { IconChevronUp } from "../icons/IconChevronUp/IconChevronUp.mjs";
import { Link } from "../Link/Link.mjs";
import { Overlay } from "../private/Overlay/Overlay.mjs";
import { Text } from "../Text/Text.mjs";
import { paginate } from "./paginate.mjs";
import { hover, background, current, lightModeCurrentKeyline, darkModeCurrentKeyline, itemsPerPage, divider } from "./Pagination.css.mjs";
import { Hidden } from "../Hidden/Hidden.mjs";
import { MenuRenderer } from "../MenuRenderer/MenuRenderer.mjs";
import { MenuItem } from "../MenuItem/MenuItem.mjs";
const borderRadius = "small";
const PageNav = ({
  label,
  direction,
  disabled
}) => {
  const isPrevious = direction === "prev";
  return /* @__PURE__ */ jsxs(
    Box,
    {
      component: "span",
      className: hover,
      position: "relative",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "xsmall",
      children: [
        /* @__PURE__ */ jsx(
          Overlay,
          {
            component: "span",
            background: "formAccentSoftHover",
            borderRadius,
            transition: "fast",
            className: background
          }
        ),
        /* @__PURE__ */ jsx(Box, { component: "span", zIndex: 1, userSelect: "none", children: /* @__PURE__ */ jsxs(Text, { tone: disabled ? "disabled" : "formAccent", children: [
          isPrevious ? /* @__PURE__ */ jsx(IconChevronLeft, {}) : null,
          /* @__PURE__ */ jsx(
            Box,
            {
              display: isPrevious ? ["none", "inline"] : void 0,
              component: "span",
              marginLeft: isPrevious ? "xxsmall" : void 0,
              marginRight: direction === "next" ? "xxsmall" : void 0,
              children: label
            }
          ),
          direction === "next" ? /* @__PURE__ */ jsx(IconChevronRight, {}) : null
        ] }) })
      ]
    }
  );
};
const tabletButtonSpacing = "xxxsmall";
const Page = ({ number, current: current$1 }) => {
  const parentBackground = useBackground();
  return /* @__PURE__ */ jsxs(
    Box,
    {
      component: "span",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      position: "relative",
      width: "touchable",
      height: "touchable",
      className: hover,
      children: [
        /* @__PURE__ */ jsx(
          Overlay,
          {
            component: "span",
            background: current$1 ? "formAccentSoftActive" : "formAccentSoftHover",
            transition: current$1 ? void 0 : "fast",
            borderRadius,
            className: [background, current$1 ? current : void 0]
          }
        ),
        /* @__PURE__ */ jsx(
          Overlay,
          {
            component: "span",
            borderRadius,
            boxShadow: "borderField",
            className: {
              [lightModeCurrentKeyline]: parentBackground.lightMode !== "surface" && current$1,
              [darkModeCurrentKeyline]: parentBackground.darkMode !== "surface" && current$1
            }
          }
        ),
        /* @__PURE__ */ jsx(Box, { component: "span", zIndex: 1, userSelect: "none", children: /* @__PURE__ */ jsx(
          Text,
          {
            baseline: false,
            align: "center",
            weight: current$1 ? "medium" : void 0,
            tone: current$1 ? "neutral" : void 0,
            children: number
          }
        ) })
      ]
    }
  );
};
function hasTotalItems(props) {
  return "totalItems" in props && props.totalItems !== void 0;
}
const DisplayPages = ({
  page,
  itemsPerPage: itemsPerPage2,
  totalItems
}) => {
  const start = (page - 1) * itemsPerPage2 + 1;
  let end = page * itemsPerPage2;
  if (page * itemsPerPage2 + 1 > totalItems) end = totalItems;
  return /* @__PURE__ */ jsxs(Text, { size: "small", children: [
    start,
    " - ",
    end,
    " of ",
    totalItems,
    /* @__PURE__ */ jsx(Hidden, { below: "tablet", children: " items" })
  ] });
};
const ItemsPerPage = ({
  messages,
  itemsPerPageOptions,
  onItemsPerPageChange,
  page,
  totalItems,
  itemsPerPage: itemsPerPage$1
}) => /* @__PURE__ */ jsxs(
  Box,
  {
    display: "flex",
    flexDirection: "row",
    gap: "xsmall",
    component: "li",
    transition: "fast",
    alignItems: "center",
    justifyContent: "center",
    className: itemsPerPage,
    children: [
      /* @__PURE__ */ jsxs(Box, { display: "flex", flexDirection: "row", gap: "xxsmall", children: [
        /* @__PURE__ */ jsx(Text, { size: "small", children: (messages == null ? void 0 : messages.itemsPerLabel) ?? "Rows per page: " }),
        /* @__PURE__ */ jsx(
          MenuRenderer,
          {
            placement: "top",
            align: "right",
            trigger: (triggerProps, { open }) => /* @__PURE__ */ jsx(Box, { userSelect: "none", cursor: "pointer", ...triggerProps, children: /* @__PURE__ */ jsxs(Text, { size: "small", children: [
              itemsPerPage$1,
              " ",
              open ? /* @__PURE__ */ jsx(IconChevronUp, {}) : /* @__PURE__ */ jsx(IconChevronDown, {})
            ] }) }),
            children: itemsPerPageOptions.map((item, index) => /* @__PURE__ */ jsx(
              MenuItem,
              {
                onClick: () => {
                  onItemsPerPageChange(item);
                },
                children: item
              },
              `item-options-${index}`
            ))
          }
        )
      ] }),
      /* @__PURE__ */ jsx(Box, { className: divider }),
      /* @__PURE__ */ jsx(DisplayPages, { page, itemsPerPage: itemsPerPage$1, totalItems })
    ]
  }
);
const defaultPageLimit = 7;
const Pagination = ({
  page,
  linkProps,
  label,
  pageLabel = (p) => `Go to page ${p}`,
  nextLabel = "Next",
  previousLabel = "Previous",
  pageLimit = defaultPageLimit,
  data,
  itemsPerPage: itemsPerPage2,
  messages,
  ...restProps
}) => {
  if (!hasTotalItems(restProps)) {
    assert(restProps.total >= 1, `\`total\` must be at least 1`);
    assert(
      page >= 1 && page <= restProps.total,
      `\`page\` must be between 1 and ${restProps.total}`
    );
    assert(
      pageLimit >= 1 && pageLimit <= defaultPageLimit,
      `\`pageLimit\` must be between 1 and ${defaultPageLimit}`
    );
  }
  const pages = paginate({
    page,
    maxPages: pageLimit,
    total: hasTotalItems(restProps) ? restProps.totalItems : restProps.total,
    itemsPerPage: itemsPerPage2
  });
  const showPrevious = page > 1;
  const showNext = hasTotalItems(restProps) ? page < Math.ceil(restProps.totalItems / itemsPerPage2) : page < restProps.total;
  return /* @__PURE__ */ jsx(
    Box,
    {
      component: "nav",
      "aria-label": label,
      ...buildDataAttributes({ data, validateRestProps: restProps }),
      children: /* @__PURE__ */ jsxs(
        Box,
        {
          component: "ul",
          display: "flex",
          flexDirection: { mobile: "column", tablet: "column", desktop: "row" },
          justifyContent: "center",
          alignItems: "center",
          gap: "gutter",
          children: [
            itemsPerPage2 && hasTotalItems(restProps) && /* @__PURE__ */ jsx(Hidden, { below: "desktop", children: /* @__PURE__ */ jsx(
              ItemsPerPage,
              {
                messages,
                itemsPerPageOptions: restProps.itemsPerPageOptions,
                onItemsPerPageChange: restProps.onItemsPerPageChange,
                page,
                totalItems: restProps.totalItems,
                itemsPerPage: itemsPerPage2
              }
            ) }),
            /* @__PURE__ */ jsxs(Box, { display: "flex", flexDirection: "row", gap: "xxxsmall", children: [
              /* @__PURE__ */ jsx(
                Box,
                {
                  component: "li",
                  display: "flex",
                  alignItems: "center",
                  paddingRight: {
                    mobile: "medium",
                    tablet: pageLimit > 2 ? tabletButtonSpacing : void 0
                  },
                  transition: "fast",
                  pointerEvents: !showPrevious ? "none" : void 0,
                  disabled: !showPrevious,
                  children: /* @__PURE__ */ jsx(
                    Link,
                    {
                      ...linkProps({ page: page - 1, type: "previous" }),
                      rel: "prev",
                      "aria-label": previousLabel,
                      "aria-disabled": !showPrevious,
                      title: previousLabel,
                      "aria-hidden": !showPrevious,
                      tabIndex: !showPrevious ? -1 : void 0,
                      children: /* @__PURE__ */ jsx(PageNav, { label: previousLabel, direction: "prev", disabled: !showPrevious })
                    }
                  )
                }
              ),
              /* @__PURE__ */ jsx(Box, { component: "li", flexGrow: 1, children: /* @__PURE__ */ jsx(Box, { component: "ul", display: "flex", justifyContent: "center", children: pages.map((pageNumber, index) => {
                const current2 = page === pageNumber;
                const isNotLast = pages.length - 1 !== index;
                return /* @__PURE__ */ jsx(
                  Box,
                  {
                    component: "li",
                    display: !current2 ? { mobile: "none", tablet: "block" } : void 0,
                    paddingRight: pageLimit > 2 && isNotLast ? {
                      tablet: tabletButtonSpacing
                    } : void 0,
                    children: /* @__PURE__ */ jsx(
                      Link,
                      {
                        ...linkProps({ page: pageNumber, type: "pageNumber" }),
                        "aria-label": pageLabel(pageNumber),
                        "aria-current": current2 ? "page" : void 0,
                        title: pageLabel(pageNumber),
                        children: /* @__PURE__ */ jsx(Page, { number: pageNumber, current: current2 })
                      }
                    )
                  },
                  pageNumber
                );
              }) }) }),
              /* @__PURE__ */ jsx(
                Box,
                {
                  component: "li",
                  display: "flex",
                  alignItems: "center",
                  paddingLeft: {
                    mobile: "medium",
                    tablet: pageLimit > 2 ? tabletButtonSpacing : void 0
                  },
                  style: { marginLeft: "auto" },
                  transition: "fast",
                  pointerEvents: !showNext ? "none" : void 0,
                  disabled: !showNext,
                  children: /* @__PURE__ */ jsx(
                    Link,
                    {
                      ...linkProps({ page: page + 1, type: "next" }),
                      rel: "next",
                      "aria-label": nextLabel,
                      "aria-disabled": !showNext,
                      title: nextLabel,
                      "aria-hidden": !showNext,
                      tabIndex: !showNext ? -1 : void 0,
                      children: /* @__PURE__ */ jsx(PageNav, { label: nextLabel, direction: "next", disabled: !showNext })
                    }
                  )
                }
              )
            ] }),
            itemsPerPage2 && hasTotalItems(restProps) && /* @__PURE__ */ jsx(Hidden, { above: "tablet", children: /* @__PURE__ */ jsx(
              ItemsPerPage,
              {
                messages,
                itemsPerPageOptions: restProps.itemsPerPageOptions,
                onItemsPerPageChange: restProps.onItemsPerPageChange,
                page,
                totalItems: restProps.totalItems,
                itemsPerPage: itemsPerPage2
              }
            ) })
          ]
        }
      )
    }
  );
};
export {
  Pagination,
  defaultPageLimit
};
